/**
 * 根据结构描述生成 Easy Email Pro 格式的邮件模板 JSON
 * 使用 BlockManager.getBlockByType(type).create(payload) 创建元素
 */

import { BlockManager } from "../../src/utils/BlockManager";
import { StandardType } from "../../src/constants/blockType";

// 定义 BasicType.PAGE 常量，避免导入整个 constants 模块
const PAGE_TYPE = "page" as const;
import type {
  SectionPayload,
  ColumnPayload,
  ElementPayload,
  GenerateEmailTemplateInput,
  GenerateEmailTemplateOutput,
} from "../types";

// ============================================
// 验证函数
// ============================================

interface ValidationError {
  path: string;
  message: string;
}

/**
 * 验证 children 数组（文本节点）
 */
function validateChildren(children: any, path: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!Array.isArray(children)) {
    errors.push({
      path,
      message: `children 必须是数组，当前类型: ${typeof children}`,
    });
    return errors;
  }

  children.forEach((item, index) => {
    if (typeof item !== "object" || item === null) {
      errors.push({
        path: `${path}[${index}]`,
        message: `节点必须是对象，当前类型: ${typeof item}`,
      });
      return;
    }

    // 检查是否是 TextNode
    if ("text" in item && typeof item.text !== "string") {
      errors.push({
        path: `${path}[${index}].text`,
        message: `text 字段必须是字符串，当前类型: ${typeof item.text}`,
      });
    }
  });

  return errors;
}

/**
 * 验证元素 Payload
 */
function validateElement(element: any, path: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!element || typeof element !== "object") {
    errors.push({
      path,
      message: `元素必须是对象，当前类型: ${typeof element}`,
    });
    return errors;
  }

  if (!element.type) {
    errors.push({
      path: `${path}.type`,
      message: "元素必须包含 type 字段",
    });
    return errors;
  }

  // 验证 type 是否是有效的 standard 类型
  const validTypes = Object.values(StandardType);
  if (!validTypes.includes(element.type)) {
    errors.push({
      path: `${path}.type`,
      message: `无效的元素类型 "${element.type}"`,
    });
    return errors;
  }

  // 验证需要 children 的元素
  const textTypes = [
    StandardType.STANDARD_TEXT,
    StandardType.STANDARD_PARAGRAPH,
    StandardType.STANDARD_H1,
    StandardType.STANDARD_H2,
    StandardType.STANDARD_H3,
    StandardType.STANDARD_H4,
    StandardType.STANDARD_BUTTON,
  ];

  if (textTypes.includes(element.type)) {
    if (!element.children || !Array.isArray(element.children)) {
      errors.push({
        path: `${path}.children`,
        message: `${element.type} 必须包含 children 数组`,
      });
    } else if (element.children.length === 0) {
      errors.push({
        path: `${path}.children`,
        message: `${element.type} 的 children 不能为空`,
      });
    } else {
      errors.push(...validateChildren(element.children, `${path}.children`));
    }
  }

  // 验证 navbar 的 children
  if (element.type === StandardType.STANDARD_NAVBAR) {
    if (!element.children || !Array.isArray(element.children)) {
      errors.push({
        path: `${path}.children`,
        message: "standard-navbar 必须包含 children 数组（导航链接）",
      });
    } else if (element.children.length === 0) {
      errors.push({
        path: `${path}.children`,
        message: "standard-navbar 的 children 不能为空",
      });
    }
  }

  // 验证 social 的 children
  if (element.type === StandardType.STANDARD_SOCIAL) {
    if (!element.children || !Array.isArray(element.children)) {
      errors.push({
        path: `${path}.children`,
        message: "standard-social 必须包含 children 数组（社交图标项）",
      });
    } else if (element.children.length === 0) {
      errors.push({
        path: `${path}.children`,
        message: "standard-social 的 children 不能为空",
      });
    }
  }

  // 验证 hero 的 children
  if (element.type === StandardType.STANDARD_HERO) {
    if (!element.children || !Array.isArray(element.children)) {
      errors.push({
        path: `${path}.children`,
        message: "standard-hero 必须包含 children 数组",
      });
    } else {
      element.children.forEach((child: any, index: number) => {
        errors.push(...validateElement(child, `${path}.children[${index}]`));
      });
    }
  }

  return errors;
}

/**
 * 验证列 Payload
 */
function validateColumn(column: any, path: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!column || typeof column !== "object") {
    errors.push({
      path,
      message: `列必须是对象，当前类型: ${typeof column}`,
    });
    return errors;
  }

  if (!Array.isArray(column.children)) {
    errors.push({
      path: `${path}.children`,
      message: "列必须包含 children 数组",
    });
    return errors;
  }

  column.children.forEach((element: any, index: number) => {
    errors.push(...validateElement(element, `${path}.children[${index}]`));
  });

  return errors;
}

/**
 * 验证区块 Payload
 */
function validateSection(section: any, path: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!section || typeof section !== "object") {
    errors.push({
      path,
      message: `区块必须是对象，当前类型: ${typeof section}`,
    });
    return errors;
  }

  if (!Array.isArray(section.children)) {
    errors.push({
      path: `${path}.children`,
      message: "区块必须包含 children 数组（列）",
    });
    return errors;
  }

  if (section.children.length === 0) {
    errors.push({
      path: `${path}.children`,
      message: "区块 children 数组不能为空，至少需要一列",
    });
    return errors;
  }

  section.children.forEach((column: any, index: number) => {
    errors.push(...validateColumn(column, `${path}.children[${index}]`));
  });

  return errors;
}

/**
 * 验证完整的邮件结构
 */
function validateEmailStructure(structure: any): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!structure || typeof structure !== "object") {
    errors.push({
      path: "structure",
      message: "structure 必须是对象",
    });
    return errors;
  }

  // 验证 page
  if (!structure.page || typeof structure.page !== "object") {
    errors.push({
      path: "structure.page",
      message: "structure 必须包含 page 对象",
    });
  }

  // 验证 sections
  if (!Array.isArray(structure.sections)) {
    errors.push({
      path: "structure.sections",
      message: "structure 必须包含 sections 数组",
    });
    return errors;
  }

  if (structure.sections.length === 0) {
    errors.push({
      path: "structure.sections",
      message: "sections 数组不能为空，至少需要一个区块",
    });
    return errors;
  }

  structure.sections.forEach((section: any, index: number) => {
    errors.push(...validateSection(section, `structure.sections[${index}]`));
  });

  return errors;
}

// ============================================
// 使用 BlockManager 创建元素
// ============================================

/**
 * 创建元素 - 通用转换函数
 */
function createElement(payload: ElementPayload): any {
  const blockDef = BlockManager.getBlockByType(payload.type as any);

  if (!blockDef?.create) {
    throw new Error(`Block type not found: ${payload.type}`);
  }

  // 递归处理 children（如 hero 内部的元素）
  let children = payload.children;
  if (payload.type === StandardType.STANDARD_HERO && Array.isArray(children)) {
    children = children.map((child: any) => {
      if (child.type && child.type.startsWith("standard-")) {
        return createElement(child as ElementPayload);
      }
      return child;
    });
  }

  // 处理 navbar 的 children
  if (
    payload.type === StandardType.STANDARD_NAVBAR &&
    Array.isArray(children)
  ) {
    children = children.map((child: any) => {
      const linkBlockDef = BlockManager.getBlockByType(
        StandardType.STANDARD_NAVBAR_LINK as any
      );
      if (linkBlockDef?.create) {
        return linkBlockDef.create({
          attributes: child.attributes,
          children: child.children || [],
        });
      }
      return child;
    });
  }

  // 处理 social 的 children
  if (
    payload.type === StandardType.STANDARD_SOCIAL &&
    Array.isArray(children)
  ) {
    children = children.map((child: any) => {
      const itemBlockDef = BlockManager.getBlockByType(
        StandardType.STANDARD_SOCIAL_ELEMENT as any
      );
      if (itemBlockDef?.create) {
        return itemBlockDef.create({
          attributes: child.attributes,
          children: child.children || [],
        });
      }
      return child;
    });
  }

  return blockDef.create({
    attributes: payload.attributes || {},
    children: children || [],
  });
}

/**
 * 创建列元素
 */
function createColumn(payload: ColumnPayload): any {
  const blockDef = BlockManager.getBlockByType(
    StandardType.STANDARD_COLUMN as any
  );

  if (!blockDef?.create) {
    throw new Error(`Block type not found: ${StandardType.STANDARD_COLUMN}`);
  }

  return blockDef.create({
    attributes: payload.attributes || {},
    children: payload.children.map(createElement),
  });
}

/**
 * 创建区块元素
 */
function createSection(payload: SectionPayload): any {
  const blockDef = BlockManager.getBlockByType(
    StandardType.STANDARD_SECTION as any
  );

  if (!blockDef?.create) {
    throw new Error(`Block type not found: ${StandardType.STANDARD_SECTION}`);
  }

  return blockDef.create({
    attributes: payload.attributes || {},
    children: payload.children.map(createColumn),
  });
}

/**
 * 主函数：根据结构描述生成完整的邮件模板
 */
export function generateEmailTemplate(
  input: GenerateEmailTemplateInput
): GenerateEmailTemplateOutput {
  try {
    const { structure } = input;

    // 验证输入结构
    const validationErrors = validateEmailStructure(structure);
    if (validationErrors.length > 0) {
      const errorMessages = validationErrors
        .map((e) => `[${e.path}] ${e.message}`)
        .join("\n");
      return {
        success: false,
        error: `结构验证失败:\n${errorMessages}`,
      };
    }

    const { page, sections } = structure;

    // 构建 page 元素
    const template = {
      type: PAGE_TYPE,
      data: {
        breakpoint: page.data?.breakpoint || "480px",
        ...page.data,
      },
      attributes: page.attributes || {},
      children: sections.map(createSection),
    };

    return {
      success: true,
      template: template as any,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export const GENERATE_EMAIL_TEMPLATE_TOOL = {
  name: "generate_email_template",
  description: `根据结构化 Payload 生成 Easy Email Pro 格式的邮件模板 JSON。

⚠️ 请先调用 get_guidelines 了解关键规则！

关键规则:
- 每个元素必须有 type 和 data 字段
- Section 的 padding 必须显式写出
- Image 的 width 只支持 px 或留空，不支持 100%

输入格式：
{
  "structure": {
    "page": {
      "type": "page",
      "attributes": { "width": "600px", "background-color": "#f5f5f5" },
      "data": { "breakpoint": "480px" }
    },
    "sections": [{
      "type": "standard-section",
      "data": {},
      "attributes": {
        "background-color": "#FFFFFF",
        "padding-top": "20px",
        "padding-bottom": "20px",
        "padding-left": "0px",
        "padding-right": "0px"
      },
      "children": [{
        "type": "standard-column",
        "data": {},
        "attributes": { "width": "100%" },
        "children": [{
          "type": "standard-paragraph",
          "data": {},
          "attributes": { "align": "center" },
          "children": [{ "text": "Hello World" }]
        }]
      }]
    }]
  }
}

输出:
- success: boolean
- template: 生成的邮件模板 JSON
- error: 错误信息（如果失败）`,
  inputSchema: {
    type: "object" as const,
    properties: {
      structure: {
        type: "object",
        description: "邮件结构 Payload",
        properties: {
          page: {
            type: "object",
            description: "页面配置",
            properties: {
              attributes: {
                type: "object",
                description: "页面属性（width, background-color 等）",
              },
              data: {
                type: "object",
                description: "页面数据（breakpoint, fonts 等）",
              },
            },
          },
          sections: {
            type: "array",
            description: "区块列表",
            items: {
              type: "object",
              properties: {
                attributes: { type: "object" },
                children: {
                  type: "array",
                  description: "列列表",
                  items: {
                    type: "object",
                    properties: {
                      attributes: { type: "object" },
                      children: {
                        type: "array",
                        description: "元素列表",
                      },
                    },
                  },
                },
              },
            },
          },
        },
        required: ["page", "sections"],
      },
    },
    required: ["structure"],
  },
};
